/**
 * 사업자 활동 로그 (Business Activity Log) API 서비스
 *
 * 백엔드 명세: BACKEND_HANDOFF_ACTIVITY_LOGS.md
 *  - GET /api/admin/activity-logs/business-options
 *  - GET /api/admin/activity-logs/business
 */

import { apiClient } from './client';
import type { TicketProductType } from './tickets';

export type { TicketProductType };

// ─── 분류 enum ───────────────────────────────────────────

/** 사업자 로그의 상위 카테고리 (필터용 그룹) */
export type BusinessLogCategory =
  | 'NOTICE_SEND'      // 알림장 발신
  | 'NOTICE_RECEIVE'   // 알림장 수신
  | 'PAYMENT'          // 결제 (모든 비즈니스 앱 결제)
  | 'TICKET_GRANT'     // 이용권 발급/회수 (이용권 사용 권한 변동)
  | 'TICKET_PRODUCT'   // 이용권 등록/삭제 (상품 카탈로그 변동)
  | 'RESERVATION'      // 예약 (신청/승인/취소)
  | 'ACCOUNT';         // 계정 (프로필, 선생님 추가/제거)

/** 사업자 로그의 세부 이벤트 코드 */
export type BusinessLogEventCode =
  | 'TICKET_ISSUE'      // 이용권 발급
  | 'TICKET_RECALL'     // 이용권 회수
  | 'TICKET_REGISTER'   // 이용권 상품 등록
  | 'TICKET_DELETE'     // 이용권 상품 삭제
  | 'RESERV_REQUEST'    // 예약 요청
  | 'RESERV_APPROVE'    // 예약 승인
  | 'RESERV_CANCEL'     // 예약 취소
  | 'ACCOUNT_ADD'       // 선생님(계정) 추가
  | 'ACCOUNT_REMOVE'    // 선생님(계정) 제거
  | 'PROFILE_PHOTO'     // 프로필 사진 등록
  | 'NOTICE_SEND'       // 알림장 발송
  | 'NOTICE_RECEIVE'    // 알림장 수신 (보호자 발신 → 사업자 수신)
  | 'PAYMENT_SUCCESS'   // 결제 성공
  | 'PAYMENT_FAIL';     // 결제 실패

/** 활동 로그 표시용 이용권 유형 라벨 — tickets.ts enum 그대로 사용 */
export const TICKET_PRODUCT_LABEL: Record<TicketProductType, string> = {
  PRODUCT_TYPE_CLASS: '수업',
  PRODUCT_TYPE_DAYCARE: '데이케어',
  PRODUCT_TYPE_HOTEL: '호텔',
  PRODUCT_TYPE_GROOMING: '미용',
  PRODUCT_TYPE_KINDERGARTEN: '유치원',
};

/** 작업 수행자 권한 */
export type BizActorRole = 'OWNER' | 'TEACHER' | 'STAFF';

export const BIZ_ACTOR_ROLE_LABEL: Record<BizActorRole, string> = {
  OWNER: '원장님',
  TEACHER: '선생님',
  STAFF: '직원',
};

export const BUSINESS_LOG_CATEGORY_LABEL: Record<BusinessLogCategory, string> = {
  NOTICE_SEND: '알림장 발신',
  NOTICE_RECEIVE: '알림장 수신',
  PAYMENT: '결제',
  TICKET_GRANT: '이용권 발급/회수',
  TICKET_PRODUCT: '이용권 등록/삭제',
  RESERVATION: '예약',
  ACCOUNT: '계정',
};

/** 세부 이벤트 → 상위 카테고리 매핑 */
export const BUSINESS_EVENT_TO_CATEGORY: Record<
  BusinessLogEventCode,
  BusinessLogCategory
> = {
  TICKET_ISSUE: 'TICKET_GRANT',
  TICKET_RECALL: 'TICKET_GRANT',
  TICKET_REGISTER: 'TICKET_PRODUCT',
  TICKET_DELETE: 'TICKET_PRODUCT',
  RESERV_REQUEST: 'RESERVATION',
  RESERV_APPROVE: 'RESERVATION',
  RESERV_CANCEL: 'RESERVATION',
  ACCOUNT_ADD: 'ACCOUNT',
  ACCOUNT_REMOVE: 'ACCOUNT',
  PROFILE_PHOTO: 'ACCOUNT',
  NOTICE_SEND: 'NOTICE_SEND',
  NOTICE_RECEIVE: 'NOTICE_RECEIVE',
  PAYMENT_SUCCESS: 'PAYMENT',
  PAYMENT_FAIL: 'PAYMENT',
};

// ─── 이벤트별 payload (discriminated union) ────────────────

interface BizLogBase {
  logSeq: number;
  /** 로그 일시 (ISO 'YYYY-MM-DDTHH:mm:ss') */
  dtLog: string;
  /** 소속 사업장 */
  companySeq: number;
  companyName: string;
  /** 작업 수행자 (사업장 소속 계정) — 시스템 이벤트는 null */
  actorBizUserSeq: number | null;
  actorName: string | null;
  actorRole: BizActorRole | null;
}

export type BusinessActivityLog = BizLogBase &
  (
    | {
        eventCode: 'TICKET_ISSUE';
        payload: {
          ticketName: string;
          productType: TicketProductType;
          petName: string;
          guardianName: string;
          /** 이용권 1매의 사용 가능 횟수 (= HIST_PURCHASE_TICKET.SPLIT_COUNT) */
          count: number;
          /**
           * 발급한 매수. 같은 발급 행위로 N장을 동시 발급한 경우 N.
           * BE 가 미전송 시 기본값 1.
           */
          quantity?: number;
        };
      }
    | {
        eventCode: 'TICKET_RECALL';
        payload: {
          ticketName: string;
          productType: TicketProductType;
          petName: string;
          guardianName: string;
          remainCount: number;
        };
      }
    | {
        eventCode: 'TICKET_REGISTER';
        payload: {
          ticketName: string;
          productType: TicketProductType;
          price: number;
          discountRate: number;
          count: number;
        };
      }
    | {
        eventCode: 'TICKET_DELETE';
        payload: {
          ticketName: string;
          productType: TicketProductType;
        };
      }
    | {
        eventCode: 'RESERV_REQUEST' | 'RESERV_APPROVE' | 'RESERV_CANCEL';
        payload: {
          petName: string;
          guardianName: string;
          ticketName: string;
        };
      }
    | {
        eventCode: 'ACCOUNT_ADD' | 'ACCOUNT_REMOVE';
        payload: {
          targetAccountName: string;
          targetRole: BizActorRole;
        };
      }
    | {
        eventCode: 'PROFILE_PHOTO';
        payload: Record<string, never>;
      }
    | {
        eventCode: 'NOTICE_SEND';
        payload: {
          sentCount: number;
        };
      }
    | {
        eventCode: 'NOTICE_RECEIVE';
        payload: {
          fromGuardianName: string;
          petName: string;
        };
      }
    | {
        eventCode: 'PAYMENT_SUCCESS' | 'PAYMENT_FAIL';
        payload: {
          productName: string;
          amount: number;
        };
      }
  );

// ─── 검색 파라미터 / 페이지 응답 ───────────────────────────

export interface BusinessActivityLogSearchRequest {
  /** 사업장 시퀀스 — 없으면 전체 */
  companySeq?: number;
  /** 시작 일자 (YYYY-MM-DD) — inclusive */
  dateFrom?: string;
  /** 종료 일자 (YYYY-MM-DD) — inclusive */
  dateTo?: string;
  /** 카테고리 다중 필터 */
  categories?: BusinessLogCategory[];
  /** 0-base 페이지 */
  page?: number;
  size?: number;
}

export interface BusinessActivityLogPage {
  content: BusinessActivityLog[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

/** 콤보박스용 사업자 옵션 */
export interface BusinessOption {
  companySeq: number;
  companyName: string;
  /** 사업장 대표(원장) 이름 */
  ownerName: string;
}

// ─── 응답 언랩 / 파라미터 정리 헬퍼 ───────────────────────

/**
 * BE 응답 `{ data: T }` 형태(또는 ServiceResult) 언랩.
 * dailyReservations.ts / tickets.ts 와 동일한 패턴.
 */
function unwrap<T>(resp: { data?: unknown }): T {
  const raw = resp.data as { data?: T } | T | undefined;
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>)
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function compactParams<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
}

// ─── API 서비스 ───────────────────────────────────────────

const BASE = '/admin/activity-logs';

export const businessActivityLogsApi = {
  /** 사업자 콤보박스 옵션 목록 */
  getBusinessOptions: async (): Promise<BusinessOption[]> => {
    const response = await apiClient.get(`${BASE}/business-options`);
    return unwrap<BusinessOption[]>(response);
  },

  /** 사업자 활동 로그 검색 */
  search: async (
    req: BusinessActivityLogSearchRequest = {},
  ): Promise<BusinessActivityLogPage> => {
    const response = await apiClient.get(`${BASE}/business`, {
      params: compactParams({
        companySeq: req.companySeq,
        dateFrom: req.dateFrom,
        dateTo: req.dateTo,
        categories: req.categories,
        page: req.page ?? 0,
        size: req.size ?? 20,
      }),
    });
    return unwrap<BusinessActivityLogPage>(response);
  },
};
